import { ClipboardList, GitBranch, Video } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/shared/utils/utils';
import { usePunchWalkthroughs } from '../hooks';

/** Route links keep each workspace bookmarkable and preserve the current project URL. */
export function PunchListsNavigation({ projectId }: { projectId?: number }) {
  const { pathname } = useLocation();
  const basePath = `${pathname.split('/punch-lists')[0]}/punch-lists`;
  const walkthroughs = usePunchWalkthroughs(projectId, 1, 'active');
  const openCount = projectId && !walkthroughs.isError ? walkthroughs.data?.count : undefined;

  return (
    <nav
      aria-label="Punch list sections"
      className="flex gap-1 overflow-x-auto border-b border-border"
    >
      {[
        { label: 'Punch items', path: '', icon: ClipboardList },
        { label: 'Workflows', path: '/workflows', icon: GitBranch },
        { label: 'AI Walkthroughs', path: '/walkthroughs', icon: Video },
      ].map(({ label, path, icon: Icon }) => (
        <NavLink
          key={path}
          to={`${basePath}${path}`}
          end
          aria-label={path === '/walkthroughs' && openCount !== undefined
            ? `${label} ${openCount} open walkthrough${openCount === 1 ? '' : 's'}`
            : label}
          className={({ isActive }) =>
            cn(
              'flex shrink-0 items-center gap-2 border-b-2 px-2 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-4',
              isActive
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
            )
          }
        >
          <Icon className="hidden size-4 sm:block" aria-hidden="true" />
          {label}
          {path === '/walkthroughs' && openCount !== undefined && (
            <span
              className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold tabular-nums text-primary"
              title={`${openCount} open walkthrough${openCount === 1 ? '' : 's'}`}
            >
              {openCount}<span className="sr-only">{` open walkthrough${openCount === 1 ? '' : 's'}`}</span>
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
